import * as React from 'react';
import styled, { css } from 'styled-components';
import debounce from 'lodash.debounce';
import { PlainButton } from '../../styled-components';
import SearchIcon from './images/search.inline.svg';
import ClearIcon from './images/clear.inline.svg';
import TextField from '../../TextField';
import SearchContext from '../../../contexts/SearchContext';
import useKeyboardEventHandler from '../../../hooks/useKeyboardEventHandler';

const SearchFieldWrapper = styled.div<{ $isSearchOpen: boolean }>`
  display: none;
  position: relative;
  margin: -10px 0;

  > svg {
    position: absolute;
    top: 50%;
    left: 20px;
    transform: translateY(-50%);
  }

  input {
    padding: 10px 50px;
    -webkit-appearance: none;
    appearance: none;

    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration {
      -webkit-appearance: none;
      appearance: none;
    }
  }

  ${({ $isSearchOpen }) => $isSearchOpen && `display: block;`}

  @media (min-width: 768px) {
    display: block;
  }
`;

const SearchButton = styled(PlainButton)<{ $isSearchOpen: boolean }>`
  display: flex;
  align-items: center;

  ${({ $isSearchOpen }) => $isSearchOpen && `display: none;`}

  @media (min-width: 768px) {
    display: none;
  }
`;

const FilterToggleButton = styled.button<{ $active: boolean; $isSearchOpen: boolean }>`
  all: unset;
  position: absolute;
  top: 50%;
  right: ${({ $isSearchOpen }) => ($isSearchOpen ? '56px' : '20px')};
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 5px;
  transition: background-color 0.2s ease-in-out;

  ${({ $active }) =>
    $active &&
    css`
      background-color: var(--earth-fm--color--green);
    `}
`;

const FilterIcon = styled.div<{ $active: boolean }>`
  position: relative;
  width: 19px;
  height: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  span {
    position: relative;
    display: block;
    width: 100%;
    height: 2px;
    background-color: var(--earth-fm--color--green);
    transition: background-color 0.2s ease-in-out;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 7.6px;
      height: 7.6px;
      transform: translateY(-50%);
      background-color: var(--earth-fm--color--beige);
      border: 2px solid var(--earth-fm--color--green);
      border-radius: 50%;
      transition: background-color 0.2s ease-in-out;
      transition-property: background-color, left, transform;
    }

    &:last-of-type::before {
      left: 100%;
      transform: translate(-100%, -50%);
    }
  }

  ${({ $active }) =>
    $active &&
    css`
      span {
        background-color: var(--earth-fm--color--yellow);

        &::before {
          left: 100%;
          transform: translate(-100%, -50%);
          background-color: var(--earth-fm--color--green);
          border-color: var(--earth-fm--color--yellow);
        }

        &:last-of-type::before {
          left: 0;
          transform: translateY(-50%);
        }
      }
    `}
`;

const ClearButton = styled(PlainButton)`
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
`;

export default function SearchBox() {
  const [searchQuery, setSearchQuery] = React.useState('');
  const {
    setQuery,
    isSearchOpen,
    isShowingFilters,
    setIsSearchOpen,
    setIsShowingFilters,
    setIsNavigatingWithKeyboard,
  } = React.useContext(SearchContext);

  const debouncedSetQuery = React.useMemo(() => debounce(setQuery, 300), [setQuery]);

  React.useEffect(() => {
    debouncedSetQuery(searchQuery);
  }, [searchQuery, debouncedSetQuery]);

  const handleClick = React.useCallback(() => {
    setIsSearchOpen(true);
  }, [setIsSearchOpen]);

  const handleFocus = React.useCallback(() => {
    setIsSearchOpen(true);
  }, [setIsSearchOpen]);

  const handleClear = React.useCallback(() => {
    setQuery('');
    setSearchQuery('');
    setIsSearchOpen(false);
    setIsShowingFilters(false);
  }, [setQuery, setIsSearchOpen, setIsShowingFilters]);

  const focusFirstItemInSearchResult = useKeyboardEventHandler('ArrowDown', () => {
    // if filter is showing and user presses arrow down key,
    // focus the first filter menu within search results
    if (isShowingFilters) {
      setIsNavigatingWithKeyboard(true);
      const searchFilterButton = document.querySelector<HTMLAnchorElement>(
        '.ais-Menu-list li:first-child a',
      );
      if (searchFilterButton) {
        searchFilterButton.focus();
      }
    }
  });

  const toggleFilters = React.useCallback(() => {
    if (isShowingFilters) {
      setIsShowingFilters(false);
    } else {
      setIsSearchOpen(true);
      setIsShowingFilters(true);
    }
  }, [isShowingFilters, setIsSearchOpen, setIsShowingFilters]);

  const clearSearchFromFilterButton = useKeyboardEventHandler('Escape', handleClear);
  const handleKeyUp = useKeyboardEventHandler(['Escape', 'Tab'], handleClear);

  return (
    <>
      <SearchFieldWrapper $isSearchOpen={isSearchOpen}>
        <SearchIcon />
        <TextField
          id="header-search-field"
          label="Search"
          labelHidden
          focused={isSearchOpen}
          value={searchQuery}
          placeholder="Search"
          type="search"
          onChange={setSearchQuery}
          onFocus={handleFocus}
          autoComplete="off"
        />
        <FilterToggleButton
          $active={isShowingFilters}
          $isSearchOpen={isSearchOpen}
          onClick={toggleFilters}
          onKeyDown={focusFirstItemInSearchResult}
          aria-label="Filter. Press enter to open filter menu. To navigate within the search results press arrow down key and then tab."
          aria-haspopup="dialog"
          onKeyUp={clearSearchFromFilterButton}
        >
          <FilterIcon $active={isShowingFilters}>
            <span />
            <span />
          </FilterIcon>
          <span className="screen-reader-text">Filter</span>
        </FilterToggleButton>
        {isSearchOpen ? (
          <ClearButton type="reset" onClick={handleClear} title="Clear" onKeyUp={handleKeyUp}>
            <ClearIcon />
            <span className="screen-reader-text">Clear</span>
          </ClearButton>
        ) : null}
      </SearchFieldWrapper>

      <SearchButton onClick={handleClick} $isSearchOpen={isSearchOpen}>
        <SearchIcon />
        <span className="screen-reader-text">Search</span>
      </SearchButton>
    </>
  );
}
